/* The platform and type switch on the product page without a page load
   (blocks/st-platform-links.liquid; Paulius, 2026-10-10: "when I click ... whatsapp or trustpilot it
   has to load a new page, can we not ... preload those items?"; the type row, 2026-10-12).

   Each platform is its own product, so the links stay real links (they work without script, and in
   a new tab). With script:
   - Preload: the other products' sections are fetched through the Section Rendering API (only the
     sections in <main>, about a tenth of the page) at once on hover, focus or touch; the first photo
     of each is warmed in the image cache. The other platforms (links marked data-st-keep-count) are
     also fetched once the page is idle, except on Save-Data or a 2g connection; the type row is
     fetched on intent only, so a phone does not download every product on arrival.
   - Click: the sections in <main> are swapped for the preloaded ones (with a cross-fade where the
     browser has view transitions and motion is welcome). Switching platform carries the count
     chosen so far over (as on the homepage buy box); switching type does not. The address and title
     change, focus moves to the new current link in the row that was used, and a custom event
     st_product_swapped is published for the store's customer-events pixel, since no new page view
     fires. Back and forward swap the same way.
   - st_product_swapped carries the new product as Meta's ViewContent needs it (TRACKING-QA.md, Matt
     2026-10-16): product_id, variant_id (the selected variant), value and currency, from the new pack
     picker's mix (assets/st-pack.js mixDetail), so with a carried count the value is the total the
     buyer sees, not one card; count and contents as in st_pack_changed (assets/st-track.js); and an
     event_id. Ids are strings, values in the currency's units. Without a picker it sends the ids
     from the product form and no value. With a value it also goes to Meta as ViewContent through
     Upstack (assets/st-upstack.js).
   - Anything unexpected (a section missing from the response, a failed fetch, a different template)
     falls back to an ordinary page load. */
var stSwapCache = (window.__stSwapCache ||= new Map()); // product path -> Promise<{ [sectionId]: html } | null>

(() => {
  if (customElements.get('st-platform-swap')) return;

  const main = () => document.getElementById('MainContent');
  const sectionIds = () =>
    [...(main()?.querySelectorAll(':scope > .shopify-section[id^="shopify-section-"]') || [])]
      .map((s) => s.id.replace(/^shopify-section-/, ''))
      .slice(0, 5);
  const pathOf = (href) => {
    const u = new URL(href, location.href);
    return u.origin === location.origin ? u.pathname : null;
  };

  function load(path) {
    if (!path) return Promise.resolve(null);
    if (stSwapCache.has(path)) return stSwapCache.get(path);
    const ids = sectionIds();
    if (!ids.length) return Promise.resolve(null);
    // No Accept: application/json header: with it Shopify answers with the product's JSON instead.
    const request = fetch(`${path}?sections=${ids.map(encodeURIComponent).join(',')}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || ids.some((id) => typeof data[id] !== 'string' || !data[id])) return null;
        warm(data, ids);
        return data;
      })
      .catch(() => null);
    stSwapCache.set(path, request);
    // A failed preload may succeed later: forget it so the next hover or click tries again.
    request.then((d) => {
      if (!d) stSwapCache.delete(path);
    });
    return request;
  }

  // Warm the first gallery photo so the swapped page shows it at once.
  function warm(data, ids) {
    for (const id of ids) {
      const doc = new DOMParser().parseFromString(data[id], 'text/html');
      const img = doc.querySelector('media-gallery img');
      if (!img) continue;
      const pre = new Image();
      if (img.getAttribute('sizes')) pre.sizes = img.getAttribute('sizes');
      if (img.getAttribute('srcset')) pre.srcset = img.getAttribute('srcset');
      pre.src = img.getAttribute('src') || '';
      return;
    }
  }

  // The product now on show, for st_product_swapped.
  function viewed() {
    const id = window.crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const d = main()?.querySelector('st-pack-picker[data-product]')?.mixDetail?.();
    if (!d) {
      const buy = main()?.querySelector('product-form-component[data-product-id]');
      return {
        product_id: buy?.dataset.productId || '',
        variant_id: buy?.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value || '',
        event_id: id,
      };
    }
    return {
      product_id: String(d.product),
      title: d.title || '',
      variant_id: String(d.variant || d.items[0]?.id || ''),
      count: d.count,
      value: Math.round(d.total) / 100,
      currency: d.currency,
      contents: d.items.map((it) => ({ id: String(it.id), quantity: it.quantity, item_price: Math.round(it.price) / 100 })),
      event_id: id,
    };
  }

  let token = 0;
  async function swap(href, { push, keep = false, row = '.st-plat' }) {
    const path = pathOf(href);
    const ids = sectionIds();
    const mine = ++token;
    const data = await load(path);
    if (mine !== token) return; // a later click or back/forward won
    if (!data) {
      if (push) location.href = href;
      else location.reload();
      return;
    }
    const oldTitle = main()?.querySelector('h1')?.textContent.trim() || '';
    const count = keep ? main()?.querySelector('st-pack-picker')?.count : undefined;
    const apply = () => {
      for (const id of ids) {
        const current = document.getElementById(`shopify-section-${id}`);
        const doc = new DOMParser().parseFromString(data[id], 'text/html');
        const fresh = doc.getElementById(`shopify-section-${id}`) || doc.body.firstElementChild;
        if (!current || !fresh) continue;
        // The count carries over: the new picker starts from it (assets/st-pack.js reads this map).
        const picker = fresh.querySelector('st-pack-picker[data-product]');
        if (picker && count && window.__stPackCounts && !window.__stPackCounts.has(picker.dataset.product)) {
          window.__stPackCounts.set(picker.dataset.product, count);
        }
        current.replaceWith(document.adoptNode(fresh));
      }
      const newTitle = main()?.querySelector('h1')?.textContent.trim() || '';
      if (oldTitle && newTitle && document.title.includes(oldTitle)) document.title = document.title.replace(oldTitle, newTitle);
      window.__stSwapShown = path;
      if (push) history.pushState({ stSwap: true }, '', href);
      main()?.querySelector(`${row}[aria-current="page"]`)?.focus({ preventScroll: true });
      try {
        const v = viewed();
        window.Shopify?.analytics?.publish?.('st_product_swapped', { page: location.pathname, ...v });
        // Meta's ViewContent for the new product, through Upstack (assets/st-upstack.js).
        if (v.contents) {
          window.stUpstackView?.({
            productId: v.product_id,
            title: v.title,
            currency: v.currency,
            value: v.value,
            items: v.contents.map((c) => ({ variant: c.id, quantity: c.quantity, price: c.item_price })),
          });
        }
      } catch (err) {}
    };
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (document.startViewTransition && !calm) document.startViewTransition(apply);
    else apply();
  }

  // Back and forward between swapped products swap again. Only entries this script pushed share the
  // document, so a popstate whose path differs from the product on show is one of ours. (Horizon's
  // variant picker replaces the entry's state with {} on a variant change, so the state is not a
  // reliable marker; the path is.)
  if (!window.__stSwapPop) {
    window.__stSwapPop = true;
    window.__stSwapShown = pathOf(location.href);
    window.addEventListener('popstate', () => {
      if (pathOf(location.href) !== window.__stSwapShown) swap(location.href, { push: false });
    });
  }

  customElements.define(
    'st-platform-swap',
    class extends HTMLElement {
      connectedCallback() {
        // In the theme editor every click must stay an ordinary page load.
        if (window.Shopify?.designMode) return;
        const links = [...this.querySelectorAll('a.st-plat[href], a.st-type[href]')].filter((a) => !a.hasAttribute('aria-current'));
        const early = (event) => load(pathOf(event.currentTarget.href));
        for (const a of links) {
          a.addEventListener('pointerenter', early);
          a.addEventListener('focus', early);
          a.addEventListener('touchstart', early, { passive: true });
          a.addEventListener('click', (event) => {
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            swap(a.href, { push: true, keep: a.hasAttribute('data-st-keep-count'), row: a.classList.contains('st-type') ? '.st-type' : '.st-plat' });
          });
        }
        const net = navigator.connection;
        if (net?.saveData || /2g/.test(net?.effectiveType || '')) return;
        const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 1200));
        const preloadAll = () => idle(() => links.filter((a) => a.hasAttribute('data-st-keep-count')).forEach((a) => load(pathOf(a.href))));
        if (document.readyState === 'complete') preloadAll();
        else window.addEventListener('load', preloadAll, { once: true });
      }
    }
  );
})();
